import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

// Optional hype/glitch sound cue synthesized with the Web Audio API.
// Default OFF (respects autoplay policies — only starts on user click).
export const SoundToggle = () => {
  const [on, setOn] = useState(false);
  const ctxRef = useRef(null);
  const intervalRef = useRef(null);

  const ensureCtx = () => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
    }
    return ctxRef.current;
  };

  const blip = (freq = 880, dur = 0.05, type = "square", gain = 0.04) => {
    const ctx = ensureCtx();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    osc.connect(g);
    g.connect(ctx.destination);
    const now = ctx.currentTime;
    g.gain.setValueAtTime(gain, now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    osc.start(now);
    osc.stop(now + dur);
  };

  const toggle = () => {
    const next = !on;
    setOn(next);
    if (next) {
      const ctx = ensureCtx();
      if (ctx.state === "suspended") ctx.resume();
      // rising hype cue
      blip(440, 0.08, "sawtooth", 0.05);
      setTimeout(() => blip(660, 0.08, "sawtooth", 0.05), 90);
      setTimeout(() => blip(900, 0.12, "sawtooth", 0.05), 180);
      // glitch tick synced to the countdown, once per second
      intervalRef.current = setInterval(() => blip(1250, 0.025, "square", 0.02), 1000);
    } else {
      clearInterval(intervalRef.current);
      blip(300, 0.1, "sine", 0.04);
    }
  };

  useEffect(
    () => () => {
      clearInterval(intervalRef.current);
      if (ctxRef.current) ctxRef.current.close();
    },
    []
  );

  return (
    <motion.button
      onClick={toggle}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.8 }}
      data-testid="sound-toggle"
      aria-label={on ? "Mute sound" : "Enable sound"}
      className={`fixed bottom-5 left-5 z-50 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 rounded-full backdrop-blur-md border transition-colors ${
        on
          ? "bg-[#39ff14] text-black border-[#39ff14]"
          : "bg-black/70 text-white/70 border-white/15 hover:border-[#39ff14] hover:text-white"
      }`}
    >
      {on ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      {on ? "Sound On" : "Sound Off"}
      {on && <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />}
    </motion.button>
  );
};
